import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CommunityClient from '../components/desktop/CommunityClient';
import LoadingScreen from '../components/desktop/LoadingScreen';
import SoftLoad from '../components/desktop/SoftLoad';

import { fetchWithAuth } from '../lib/fetchWithAuth';
export default function CommunityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tabContent, setTabContent] = useState<any>(null);
  const [isLoadingTab, setIsLoadingTab] = useState(false);

  const activeTab = (searchParams.get('tab') || 'public') as 'public' | 'members' | 'staff' | 'forum' | 'chat';

  // Reset state when route parameters change
  useEffect(() => {
    setLoading(true);
    setCommunity(null);
    setCurrentUser(null);
    setTabContent(null);
  }, [id, activeTab]);

  // Apply name/description/avatar saved from the theme customizer without a reload.
  useEffect(() => {
    const handleInfoUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const saved = detail.community;
      if (!saved) return;
      setCommunity((prev: any) => {
        if (!prev || prev.id !== detail.communityId) return prev;
        const next = { ...prev };
        if (saved.name !== undefined) next.name = saved.name;
        if (saved.nameSlug !== undefined) next.nameSlug = saved.nameSlug;
        if (saved.description !== undefined) next.description = saved.description;
        if (saved.avatar !== undefined) next.avatar = saved.avatar;
        if (saved.banner !== undefined) next.banner = saved.banner;
        if (saved.communityCustomization !== undefined) next.communityCustomization = saved.communityCustomization;
        return next;
      });
    };
    window.addEventListener('communityInfoUpdated', handleInfoUpdated);
    return () => window.removeEventListener('communityInfoUpdated', handleInfoUpdated);
  }, []);

  useEffect(() => {
    async function loadData() {
      if (!id) {
        navigate('/community');
        return;
      }

      // Wait for auth to finish loading before proceeding
      if (authLoading) {
        return;
      }

      // Reset state before loading new community
      setLoading(true);
      setCommunity(null);
      setCurrentUser(null);
      setTabContent(null);

      try {
        // Fetch community data by slug from API
        const communityResponse = await fetchWithAuth(`/api/communities/slug/${id}`, {
          credentials: 'include'
        });

        if (!communityResponse.ok) {
          throw new Error('Community not found');
        }

        const communityData = await communityResponse.json();
        const comm = communityData.community || communityData;

        setCommunity({
          id: comm.id,
          name: comm.name,
          nameSlug: comm.nameSlug,
          description: comm.description,
          avatar: comm.avatar,
          banner: comm.banner,
          profileDecoration: comm.profileDecoration,
          headerDesign: comm.headerDesign,
          isPublic: comm.accessType === 'public',
          customCards: comm.customCards || [],
          memberCount: comm.memberCount || comm._count?.members || 0,
          postCount: comm.postCount || comm._count?.posts || 0,
          threadCount: comm.threadCount || comm._count?.threads || 0,
          chatCount: comm.chatCount || 0,
          communityCustomization: comm.communityCustomization || {},
          currentUserRole: comm.currentUserRole,
          members: comm.members || [],
        });

        if (authUser) {
          setCurrentUser({
            id: authUser.id.toString(),
            username: authUser.username,
            displayName: authUser.name || authUser.username,
            avatar: authUser.profilePicture,
            membership: comm.currentUserRole ? { role: comm.currentUserRole } : null,
            isAdmin: authUser.isAdmin || comm.currentUserRole === 'owner' || comm.currentUserRole === 'admin',
            isModerator: authUser.isModerator || comm.currentUserRole === 'moderator',
            isCommunityAdmin: comm.currentUserRole === 'owner' || comm.currentUserRole === 'admin',
            isCommunityModerator: comm.currentUserRole === 'moderator',
            communityRole: comm.currentUserRole,
          });

          // Check if user is admin (lowercase role values from Prisma)
          setIsAdmin(comm.currentUserRole === 'owner' || comm.currentUserRole === 'admin');
        }

        // Fetch tab content based on active tab
        let newTabContent = [];
        if (activeTab === 'public' || activeTab === 'members' || activeTab === 'staff') {
          const postsResponse = await fetchWithAuth(`/api/communities/${comm.id}/posts?tab=${activeTab}`, {
            credentials: 'include'
          });
          if (postsResponse.ok) {
            const postsData = await postsResponse.json();
            newTabContent = postsData.posts || [];
          }
        } else if (activeTab === 'forum') {
          const forumResponse = await fetchWithAuth(`/api/communities/${comm.id}/posts?postType=forum`, {
            credentials: 'include'
          });
          if (forumResponse.ok) {
            const forumData = await forumResponse.json();
            newTabContent = forumData.posts || [];
          } else {
          }
        } else if (activeTab === 'chat') {
          const chatResponse = await fetchWithAuth(`/api/communities/${comm.id}/chat`, {
            credentials: 'include'
          });
          if (chatResponse.ok) {
            const chatData = await chatResponse.json();
            newTabContent = chatData.messages || [];
          }
        }

        setTabContent(newTabContent);
      } catch (error) {
        // Don't redirect, just show error state
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id, navigate, authUser, authLoading, activeTab]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!community) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-bg)', color: 'var(--text-color)' }}>
        <div className="text-xl">Community not found</div>
      </div>
    );
  }

  // Don't render CommunityClient if tabContent is not ready for the current tab
  // Also verify that tabContent has the correct structure for the active tab
  const isTabContentValid = () => {
    if (!tabContent || !Array.isArray(tabContent)) {
      return false;
    }

    // Allow empty arrays
    if (tabContent.length === 0) {
      return true;
    }

    // Validate forum tab has proper structure
    if (activeTab === 'forum') {
      // Forum posts should have a title - _count is optional
      const isValid = tabContent[0]?.title !== undefined;
      return isValid;
    }

    // Validate chat tab has proper structure
    if (activeTab === 'chat') {
      const isValid = tabContent[0]?.content !== undefined && tabContent[0]?.author !== undefined;
      return isValid;
    }

    // For post tabs, just check it's an array
    return true;
  };

  if (!isTabContentValid()) {
    // Show soft loading for tab switches - don't obstruct the UI
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <SoftLoad message="Loading..." />
      </div>
    );
  }


  return (
    <CommunityClient
      key={id}
      community={community}
      currentUser={currentUser}
      activeTab={activeTab}
      tabContent={tabContent}
      isAdmin={isAdmin}
    />
  );
}
